import { useCallback, useMemo, useState } from 'react';

const DEFAULT_PER_PAGE = 10;

/**
 * Client-side page slice + Pagination props for the inbox list.
 * Resets to page 1 when `resetKey` changes (scope / filters).
 */
export function useInboxPagination(items, resetKey) {
  const [pagination, setPagination] = useState({ page: 1, resetKey });
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const page = pagination.resetKey === resetKey ? pagination.page : 1;

  const setPage = useCallback(
    (updater) => {
      setPagination((prev) => {
        const currentPage = prev.resetKey === resetKey ? prev.page : 1;
        const nextPage = typeof updater === 'function' ? updater(currentPage) : updater;
        return { page: nextPage, resetKey };
      });
    },
    [resetKey],
  );

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage) || 1);
  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return items.slice(start, start + perPage);
  }, [items, safePage, perPage]);

  const rangeLabel = useMemo(() => {
    if (totalItems === 0) return '0–0';
    const from = (safePage - 1) * perPage + 1;
    const to = Math.min(safePage * perPage, totalItems);
    return `${from}–${to}`;
  }, [safePage, perPage, totalItems]);

  const onPrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, [setPage]);

  const onNextPage = useCallback(() => {
    setPage((p) => Math.min(totalPages, p + 1));
  }, [setPage, totalPages]);

  const onPageSizeChange = useCallback((size) => {
    setPerPage(size);
    setPage(1);
  }, [setPage]);

  return {
    pageItems,
    rangeLabel,
    totalItems,
    safePage,
    totalPages,
    perPage,
    hasPrevious: safePage > 1,
    hasNext: safePage < totalPages,
    onPrevPage,
    onNextPage,
    onPageSizeChange,
  };
}
